import { RadioCardGroup, CurrencyInput, FormInput } from '../forms';
import {
  isValidCreditScore,
  parseCreditScoreInput,
} from '../../utils/creditScoreInput';
import { getCountyOptionsForZip } from '../../utils/propertyLocationLookup';
import PropertyLocationInput from './PropertyLocationInput';
import FieldHelpButton from './FieldHelpButton';

const QUESTION_HELP_KEY = '__question__';

const InputWithHelp = ({
  children,
  onFieldHelp,
  activeHelpValue,
  helpLabel,
}) => {
  if (!onFieldHelp) return children;

  return (
    <div className="lk-conv-field-with-help">
      <div className="lk-conv-field-with-help__control">{children}</div>
      <FieldHelpButton
        active={activeHelpValue === QUESTION_HELP_KEY}
        label={helpLabel || 'What does this mean?'}
        onClick={() => onFieldHelp(null, null)}
      />
    </div>
  );
};

const ConversationalQuestionInput = ({
  questionDef,
  value,
  onChange,
  onSubmit,
  answers = {},
  onFieldHelp,
  activeHelpValue,
}) => {
  if (!questionDef) return null;

  const { type, options, placeholder, question } = questionDef;
  const helpLabel = question ? `Help: ${question}` : 'What does this mean?';

  if (type === 'radio' && options) {
    return (
      <RadioCardGroup
        conversational
        value={value}
        activeHelpValue={activeHelpValue}
        onOptionHelpClick={onFieldHelp ? (option) => onFieldHelp(option.value, option.label) : undefined}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          onSubmit?.(next);
        }}
        options={options.map((opt) => ({
          value: opt.value,
          label: opt.label,
          description: opt.description,
        }))}
      />
    );
  }

  if (type === 'propertyLocation') {
    return (
      <PropertyLocationInput
        value={value}
        onChange={onChange}
        onSubmit={onSubmit}
        onFieldHelp={onFieldHelp}
        activeHelpValue={activeHelpValue}
        helpLabel={helpLabel}
      />
    );
  }

  if (type === 'countySelect') {
    const countyOptions = getCountyOptionsForZip(answers.propertyZip);
    return (
      <RadioCardGroup
        conversational
        value={value}
        activeHelpValue={activeHelpValue}
        onOptionHelpClick={onFieldHelp ? (option) => onFieldHelp(option.value, option.label) : undefined}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          onSubmit?.(next);
        }}
        options={countyOptions}
      />
    );
  }

  if (type === 'creditScore') {
    const display = parseCreditScoreInput(value);
    const valid = isValidCreditScore(display);
    const showError = display.length === 3 && !valid;

    return (
      <InputWithHelp
        onFieldHelp={onFieldHelp}
        activeHelpValue={activeHelpValue}
        helpLabel={helpLabel}
      >
        <div className="lk-conv-credit-score">
          <FormInput
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={3}
            value={display}
            onChange={(e) => onChange(parseCreditScoreInput(e.target.value))}
            placeholder={placeholder || 'e.g. 720'}
            aria-invalid={showError}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid) onSubmit?.(display);
            }}
            onBlur={() => {
              if (valid) onSubmit?.(display);
            }}
          />
          {showError && (
            <p className="lk-conv-credit-score__error" role="alert">
              That score isn&apos;t valid — tap the help icon to see what I can accept.
            </p>
          )}
        </div>
      </InputWithHelp>
    );
  }

  if (type === 'currency') {
    return (
      <InputWithHelp
        onFieldHelp={onFieldHelp}
        activeHelpValue={activeHelpValue}
        helpLabel={helpLabel}
      >
        <CurrencyInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value) onSubmit?.(value);
          }}
          onBlur={() => {
            if (value) onSubmit?.(value);
          }}
        />
      </InputWithHelp>
    );
  }

  return (
    <InputWithHelp
      onFieldHelp={onFieldHelp}
      activeHelpValue={activeHelpValue}
      helpLabel={helpLabel}
    >
      <FormInput
        type={
          type === 'email' ? 'email'
            : type === 'tel' ? 'tel'
              : type === 'date' ? 'date'
                : type === 'number' ? 'number'
                  : 'text'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value) onSubmit?.(value);
        }}
        onBlur={() => {
          if (value) onSubmit?.(value);
        }}
      />
    </InputWithHelp>
  );
};

export default ConversationalQuestionInput;
